/**
 * login-password/index.tsx  — Auth0 ACUL screen: "login-password"
 *
 * WHAT THIS SCREEN DOES
 * ---------------------
 * Shows the password entry step after the user's identifier was accepted by
 * the `login-id` screen.  The identifier is read from sessionStorage so it
 * can be displayed ("Logging in as jane@example.com").
 *
 * Auth0 provides the `data.username` field on this screen's context, which
 * is the canonical source.  sessionStorage is a UX fallback for the display.
 *
 * ALTERNATE METHOD
 * ----------------
 * A secondary "Use a one-time code instead" link is shown.
 * Clicking it calls redirectToPasswordless() from src/utils/navigation.ts
 * (requires domain / clientId from the UL transaction context — see TODOs).
 *
 * BACK NAVIGATION
 * ---------------
 * Auth0's `editIdentifierLink` is used when available.  Fallback is
 * window.history.back() which may not work in all UL iframe contexts.
 * TODO: verify `editIdentifierLink` behavior in your tenant.
 *
 * HOW TO EXTEND
 * -------------
 * To add social logins, re-enable <AlternativeLogins /> and wire up
 * `handleFederatedLogin` from useLoginPasswordManager.
 */

import { AuthLayout } from "@/components/auth/AuthLayout";
import { PasswordForm } from "@/components/auth/PasswordForm";
import { loadIdentifier } from "@/utils/navigation";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";
import { useErrors } from "@auth0/auth0-acul-react/login-password";
import { useLoginPasswordManager } from "./hooks/useLoginPasswordManager";

function LoginPasswordScreen() {
  const {
    loginPassword,
    texts,
    locales,
    editIdentifierLink,
    resetPasswordLink,
    data,
    handleLoginPassword,
    handleSwitchToPasswordless,
    detectIdentifierType
  } = useLoginPasswordManager();

  document.title = texts?.pageTitle || locales?.page?.title || "Enter password";
  applyAuth0Theme(loginPassword);

  // The identifier shown on this screen — prefer Auth0 SDK data, fall back to session
  const identifier: string =
    ((data as Record<string, unknown> | undefined)?.username as string) ??
    loadIdentifier();

  const { errors } = useErrors();
  const firstError = errors.byType("auth0")[0]?.message ?? undefined;

  const handleSubmit = ({ password }: { password: string }) => {
    handleLoginPassword({ username: identifier, password });
  };

  const handleBack = () => {
    if (editIdentifierLink) {
      window.location.href = editIdentifierLink;
    } else {
      window.history.back();
    }
  };

  // TODO: wire redirectToPasswordless() here with real transaction params
  const handleSwitchToOtp = () => {
    const identifierType = detectIdentifierType(identifier);
    handleSwitchToPasswordless(
      identifierType === "undefined" ? undefined : identifierType
    );
  };

  return (
    <AuthLayout title="Log in">
      <PasswordForm
        identifier={identifier}
        onSubmit={handleSubmit}
        onBack={handleBack}
        forgotPasswordHref={resetPasswordLink ?? undefined}
        isLoading={false}
        errorMessage={firstError}
        alternateLabel="Use a one-time code instead"
        onAlternate={handleSwitchToOtp}
        submitLabel="Log in"
      />
    </AuthLayout>
  );
}

export default LoginPasswordScreen;
